import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { LanguageFlag } from './LanguageFlag';
import {
  SESSION_LANGUAGE_OPTIONS,
} from './sessionFormOptions';
import type { Language } from '@business-game/shared';

export type ProgramSlotDraft = {
  slotId?: number;
  label: string;
  scheduledAt: string;
  language: Language;
  timezone: string;
  /** Ville / lieu ('' = aucun). */
  location: string;
  /** Animateur / facilitateur ('' = aucun). */
  facilitator: string;
};

type CalendarCell = {
  isoDate: string;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isPast: boolean;
};

const getTodayIso = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const isPastDate = (isoDate: string): boolean => isoDate < getTodayIso();

type Props = {
  slots: ProgramSlotDraft[];
  defaultSlotLabel: (index: number) => string;
  defaultLanguage?: Language;
  defaultTimezone?: string;
  defaultTime?: string;
  defaultLocation?: string;
  defaultFacilitator?: string;
  sessionCodes?: Record<number, string | null | undefined>;
  selectedIndex?: number | null;
  onSelectIndex?: (index: number | null) => void;
  compact?: boolean;
  onSlotsChange: (slots: ProgramSlotDraft[]) => void;
  onDuplicate: (index: number) => void;
  /** Mode sélection multiple : cliquer un jour le coche au lieu d'ajouter une séance. */
  multiSelectMode?: boolean;
  multiSelectedDates?: string[];
  onToggleMultiDate?: (isoDate: string) => void;
  /** Marqueur "exception" (séance qui diffère du modèle). */
  isException?: (slot: ProgramSlotDraft) => boolean;
};

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DEFAULT_TIMEZONE = 'Europe/Paris';

const buildMonthCells = (monthStart: Date): CalendarCell[] => {
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const offset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();
  const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;
  const todayIso = getTodayIso();
  const cells: CalendarCell[] = [];

  for (let index = 0; index < totalCells; index += 1) {
    const cellDate = new Date(year, month, index - offset + 1);
    const isoDate = `${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, '0')}-${String(cellDate.getDate()).padStart(2, '0')}`;

    cells.push({
      isoDate,
      day: cellDate.getDate(),
      isCurrentMonth: cellDate.getMonth() === month,
      isToday: isoDate === todayIso,
      isPast: isoDate < todayIso,
    });
  }

  return cells;
};

const formatTime = (scheduledAt: string) => {
  if (!scheduledAt || scheduledAt.length < 16) return '';
  return scheduledAt.slice(11, 16);
};

export const ProgramSessionsCalendar: React.FC<Props> = ({
  slots,
  defaultSlotLabel,
  defaultLanguage = 'fr',
  defaultTimezone = DEFAULT_TIMEZONE,
  defaultTime = '09:00',
  defaultLocation = '',
  defaultFacilitator = '',
  sessionCodes = {},
  selectedIndex = null,
  onSelectIndex,
  compact = false,
  onSlotsChange,
  multiSelectMode = false,
  multiSelectedDates = [],
  onToggleMultiDate,
  isException,
}) => {
  const initialMonth = useMemo(() => {
    const dated = slots.find((slot) => slot.scheduledAt);
    if (dated?.scheduledAt) {
      const d = new Date(dated.scheduledAt);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  }, []);

  const [monthStart, setMonthStart] = useState(initialMonth);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetDate, setDropTargetDate] = useState<string | null>(null);

  const cells = useMemo(() => buildMonthCells(monthStart), [monthStart]);

  const slotsByDate = useMemo(() => {
    const map = new Map<string, Array<{ slot: ProgramSlotDraft; index: number }>>();

    slots.forEach((slot, index) => {
      if (!slot.scheduledAt) return;
      const isoDate = slot.scheduledAt.slice(0, 10);
      const entries = map.get(isoDate) ?? [];
      entries.push({ slot, index });
      map.set(isoDate, entries);
    });

    return map;
  }, [slots]);

  const monthTitle = monthStart.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const addSessionOnDay = (isoDate: string) => {
    if (isPastDate(isoDate)) return;
    const existingOnDay = slotsByDate.get(isoDate)?.length ?? 0;
    const [baseHour, baseMinute] = defaultTime.split(':').map(Number);
    const hour = Math.min((baseHour ?? 9) + existingOnDay, 18);
    const time = `${String(hour).padStart(2, '0')}:${String(baseMinute ?? 0).padStart(2, '0')}`;
    const template = slots[selectedIndex ?? 0];

    onSlotsChange([
      ...slots,
      {
        label: defaultSlotLabel(slots.length),
        scheduledAt: `${isoDate}T${time}`,
        language: template?.language ?? defaultLanguage,
        timezone: template?.timezone ?? defaultTimezone,
        location: template?.location ?? defaultLocation,
        facilitator: template?.facilitator ?? defaultFacilitator,
      },
    ]);
    onSelectIndex?.(slots.length);
  };

  const updateSlot = (index: number, patch: Partial<ProgramSlotDraft>) => {
    const next = [...slots];
    next[index] = { ...next[index], ...patch };
    onSlotsChange(next);
  };

  const moveSlotToDate = (index: number, targetIsoDate: string) => {
    if (isPastDate(targetIsoDate)) return;
    const slot = slots[index];
    if (!slot?.scheduledAt) return;

    const timePart = slot.scheduledAt.includes('T') ? slot.scheduledAt.slice(11) : '09:00';
    updateSlot(index, { scheduledAt: `${targetIsoDate}T${timePart}` });
  };

  const clearDragState = () => {
    setDraggedIndex(null);
    setDropTargetDate(null);
  };

  return (
    <div className={compact ? 'py-1' : 'py-2'}>
      <div className={`flex items-center justify-between gap-3 ${compact ? 'mb-2' : 'mb-4'}`}>
        <button
          type="button"
          onClick={() => setMonthStart((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
          className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 shadow-sm hover:border-indigo-200 hover:bg-indigo-50"
          aria-label="Mois précédent"
        >
          <ChevronLeft className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        </button>
        <div className="text-center">
          <p className={`font-black capitalize text-slate-900 ${compact ? 'text-sm' : 'text-base'}`}>{monthTitle}</p>
          {!compact && (
            <p className="text-xs font-medium text-slate-500">Fuseau horaire défini par session</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setMonthStart((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
          className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 shadow-sm hover:border-indigo-200 hover:bg-indigo-50"
          aria-label="Mois suivant"
        >
          <ChevronRight className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        </button>
      </div>

      <div className={`mb-1.5 grid grid-cols-7 text-center font-bold uppercase tracking-wider text-slate-400 ${compact ? 'gap-0.5 text-[9px]' : 'gap-1 text-[11px]'}`}>
        {WEEKDAYS.map((day) => (
          <div key={day} className="py-1">
            {compact ? day.slice(0, 1) : day}
          </div>
        ))}
      </div>

      <div className={`grid grid-cols-7 ${compact ? 'gap-1' : 'gap-1.5'}`}>
        {cells.map((cell) => {
          const daySlots = slotsByDate.get(cell.isoDate) ?? [];
          const isDropTarget = dropTargetDate === cell.isoDate && !cell.isPast;
          const hasSlots = daySlots.length > 0;
          const isEmptyFuture = !cell.isPast && cell.isCurrentMonth && !hasSlots;
          const isMultiSelected = multiSelectMode && multiSelectedDates.includes(cell.isoDate);

          const cellClass = (() => {
            if (cell.isPast) {
              return 'cursor-not-allowed border-slate-200 bg-slate-100/90 text-slate-400 opacity-60';
            }
            if (isMultiSelected) {
              return 'cursor-pointer border-2 border-indigo-500 bg-indigo-100 shadow-md shadow-indigo-200/60';
            }
            if (!cell.isCurrentMonth) {
              return multiSelectMode
                ? 'cursor-pointer border-slate-100 bg-slate-50/40 text-slate-400 hover:border-indigo-200'
                : 'border-slate-100 bg-slate-50/40 text-slate-400';
            }
            if (isDropTarget) {
              return 'border-2 border-indigo-500 bg-indigo-100 shadow-md shadow-indigo-200/50';
            }
            if (hasSlots) {
              return 'border-indigo-200 bg-indigo-50/60 shadow-sm hover:border-indigo-300 hover:bg-indigo-50';
            }
            if (cell.isToday) {
              return 'cursor-pointer border-2 border-indigo-500 bg-white shadow-sm shadow-indigo-100 hover:bg-indigo-50/50';
            }
            if (isEmptyFuture) {
              return 'cursor-pointer border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40';
            }
            return 'border border-slate-200 bg-white';
          })();

          return (
            <div
              key={cell.isoDate}
              onClick={() => {
                if (cell.isPast) return;
                if (multiSelectMode) {
                  onToggleMultiDate?.(cell.isoDate);
                  return;
                }
                if (daySlots.length === 0) addSessionOnDay(cell.isoDate);
              }}
              onDragOver={(event) => {
                if (draggedIndex === null) return;
                if (cell.isPast) {
                  event.dataTransfer.dropEffect = 'none';
                  return;
                }
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                setDropTargetDate(cell.isoDate);
              }}
              onDrop={(event) => {
                if (cell.isPast) return;
                event.preventDefault();
                event.stopPropagation();
                const rawIndex = event.dataTransfer.getData('text/plain');
                const index = rawIndex ? Number(rawIndex) : draggedIndex;
                if (index !== null && !Number.isNaN(index)) {
                  moveSlotToDate(index, cell.isoDate);
                }
                clearDragState();
              }}
              className={[
                'group rounded-xl p-1.5 text-left transition-all',
                compact ? 'min-h-[52px]' : 'min-h-[84px]',
                cellClass,
              ].join(' ')}
            >
              <div className="mb-0.5 flex items-center justify-between">
                <span
                  className={[
                    'flex h-6 w-6 items-center justify-center rounded-lg text-xs font-bold',
                    cell.isToday && !cell.isPast
                      ? 'bg-indigo-600 text-white'
                      : hasSlots && !cell.isPast
                        ? 'bg-indigo-100 text-indigo-700'
                        : cell.isPast
                          ? 'text-slate-400'
                          : cell.isCurrentMonth
                            ? 'text-slate-700'
                            : 'text-slate-400',
                  ].join(' ')}
                >
                  {cell.day}
                </span>
                {!cell.isPast && cell.isCurrentMonth && !multiSelectMode ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      addSessionOnDay(cell.isoDate);
                    }}
                    className="rounded-md border border-indigo-200 bg-white p-0.5 text-indigo-600 opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
                    title="Ajouter une séance ce jour"
                    aria-label="Ajouter une séance ce jour"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                ) : null}
              </div>
              <div className={compact ? 'space-y-0.5' : 'space-y-1'}>
                {daySlots.map(({ slot, index }) => {
                  const lang = SESSION_LANGUAGE_OPTIONS.find((opt) => opt.value === slot.language);
                  const isSelected = selectedIndex === index;
                  const isDragging = draggedIndex === index;

                  return (
                    <div
                      key={slot.slotId ?? `slot-${index}`}
                      draggable
                      role="button"
                      tabIndex={0}
                      onDragStart={(event) => {
                        event.stopPropagation();
                        setDraggedIndex(index);
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', String(index));
                      }}
                      onDragEnd={clearDragState}
                      onDragOver={(event) => {
                        if (draggedIndex === null || draggedIndex === index || cell.isPast) return;
                        event.preventDefault();
                        event.stopPropagation();
                        event.dataTransfer.dropEffect = 'move';
                        setDropTargetDate(cell.isoDate);
                      }}
                      onDrop={(event) => {
                        if (draggedIndex === null || draggedIndex === index || cell.isPast) return;
                        event.preventDefault();
                        event.stopPropagation();
                        moveSlotToDate(draggedIndex, cell.isoDate);
                        clearDragState();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectIndex?.(index);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          onSelectIndex?.(index);
                        }
                      }}
                      className={[
                        'flex cursor-grab items-center gap-1 rounded-md border px-1 py-0.5 font-bold leading-tight active:cursor-grabbing',
                        compact ? 'text-[9px]' : 'text-[10px]',
                        isDragging ? 'opacity-40' : '',
                        isSelected
                          ? 'border-indigo-700 bg-indigo-600 text-white shadow-sm'
                          : cell.isPast
                            ? 'border-slate-200 bg-slate-200/80 text-slate-500'
                            : 'border-indigo-100 bg-white text-indigo-800 shadow-sm hover:border-indigo-200 hover:bg-indigo-100',
                      ].join(' ')}
                      title="Glisser-déposer pour déplacer · Cliquer pour éditer"
                    >
                      {lang && (
                        <LanguageFlag
                          countryCode={lang.countryCode}
                          title={lang.label}
                          className="h-3 w-4 shrink-0 rounded-sm"
                        />
                      )}
                      <span className="truncate">
                        {slot.slotId && sessionCodes[slot.slotId] ? (
                          <span className="font-mono">{sessionCodes[slot.slotId]} · </span>
                        ) : null}
                        {formatTime(slot.scheduledAt)} {slot.label || defaultSlotLabel(index)}
                      </span>
                      {isException?.(slot) && (
                        <span
                          className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                          title="Exception : diffère du modèle"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!compact && (
        <div className="mt-4 flex flex-wrap gap-4 text-xs font-medium text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded border border-indigo-200 bg-indigo-50" />
            Séance planifiée
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded border-2 border-indigo-500 bg-white" />
            Aujourd'hui
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded border border-dashed border-slate-300 bg-white" />
            Cliquer pour ajouter
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            Exception (diffère du modèle)
          </span>
        </div>
      )}
    </div>
  );
};
